import React, {
  memo,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import moment from "moment";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import PackageAssignFlow from "./PackageAssignFlow";
import MemberPackageStatus from "./MemberPackageStatus";
import MonthCalendarGrid, { buildMonthDays } from "./MonthCalendarGrid";
import MembersSortControl, { compareMembersBy } from "./MembersSortControl";
import { useAlert } from "../../../context/AlertContext";
import {
  useMembers,
  refreshClientPackages,
} from "../../../data/membersStore";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import {
  MORNING_HOURS,
  AFTERNOON_HOURS,
  getSlotCapacityRules,
} from "../../../../common/utils/capacity";
import { selectCurrentPackage } from "../../../../common/utils/packageStatus";
import { WEEKDAY_SHORT } from "../../../../common/utils/weekdayShort";
import "./newAppointment.css";

// Search matches by keystroke are local (already-cached client array), so
// this debounce isn't masking network latency — it exists so typing fast
// doesn't re-filter + re-render the whole (~hundreds of members) list on
// every single keystroke, only once typing pauses.
const SEARCH_DEBOUNCE_MS = 300;

// An active package with fewer than this many sessions left still keeps the "Dodeli paket"
// section visible under the selected member, so the trainer can renew/assign a fresh one right
// away instead of only finding out mid-booking that it's about to run out. At/above it the
// section stays hidden (the inline count in the member row is enough).
const LOW_SESSIONS_THRESHOLD = 4;

const memberAvatarSx = (gender) => ({
  width: 32,
  height: 32,
  fontSize: "12px",
  fontWeight: 600,
  color: "black",
  backgroundColor: gender === "Male" ? "#97dfff" : "#FF8E8B",
});

// Shared, memoized row used by both the desktop dropdown and the mobile
// step-1 list — extracted so typing in search (which re-renders the parent
// on every keystroke) doesn't re-render every row, and so the two
// previously-duplicated inline implementations stay in sync.
const MemberResultRow = memo(function MemberResultRow({
  client,
  pkg,
  isSelected,
  onSelect,
  // Mobile only: for the currently-selected row, the "X/Y · N termina" count badge shown in
  // place of the plain package-name pill (so the selected member's row reads the same as a
  // member who already has a package assigned). Null everywhere else.
  inlineBadge,
}) {
  return (
    <div
      className={`hf-naf-member-row${isSelected ? " selected" : ""}`}
      // Keeps the search input focused through the tap (blur fires on mousedown/touchstart
      // for the previously-focused element, before this row's own onClick runs) — without this,
      // tapping a result while the Mobile keyboard is still open would dismiss the keyboard and
      // snap the popup back to its normal (non keyboard-open) size/position mid-tap, right as
      // the finger is still on the screen.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onSelect(client)}
    >
      <Avatar sx={memberAvatarSx(client.gender)}>
        {client.first_name ? client.first_name[0].toUpperCase() : "?"}
        {client.last_name ? client.last_name[0].toUpperCase() : "?"}
      </Avatar>
      <div className="hf-naf-member-info">
        <span className="hf-naf-member-name">
          {client.first_name} {client.last_name}
        </span>
        <span className="hf-naf-member-contact">
          {client.email || client.phone || ""}
        </span>
      </div>
      {inlineBadge ||
        (pkg && (
          <span className="hf-naf-member-package">{pkg.package_name}</span>
        ))}
    </div>
  );
});

const MemberRowSkeleton = () => (
  <div className="hf-naf-member-row hf-naf-member-row-skeleton" aria-hidden="true">
    <div className="hf-naf-skel hf-naf-skel-avatar" />
    <div className="hf-naf-member-info">
      <div className="hf-naf-skel hf-naf-skel-line" style={{ width: "50%" }} />
      <div
        className="hf-naf-skel hf-naf-skel-line"
        style={{ width: "70%", marginTop: 4 }}
      />
    </div>
  </div>
);

const CANONICAL_HOURS = [...MORNING_HOURS, ...AFTERNOON_HOURS];

const STEP_TITLES = {
  1: "Izaberite člana",
  2: "Izaberite datum",
  3: "Izaberite termin",
  4: "Potvrda",
};

// Tablet/Desktop gets the single-page form (matching the reference design);
// Mobile keeps the existing 4-step wizard untouched. Tracked reactively so
// resizing the window (or rotating a tablet) switches layouts live.
const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

const NewAppointmentFlow = ({
  onClose,
  initialDate,
  initialHour,
  refresh,
  editingClient,
  editingAppointment,
  // Bulk-scheduling experiment (this branch only): strips this flow down to
  // just the member-search step — no Datum/Vreme/Save here at all. Picking a
  // member calls `onMemberChosen` instead of saving anything; the calendar
  // itself becomes the slot picker afterwards (see SchedulerTest.jsx).
  bulkMode = false,
  onMemberChosen,
  // "Dodaj vežbača · <slot>" shortcut (Booked Members popup → SchedulerTest.jsx's
  // handleAddMemberToSlot): `initialDate`/`initialHour` are the exact slot the trainer already
  // had open, and — unlike a plain "Novi termin" open, where those same two props only *seed* an
  // editable Datum/Vreme — this flow isn't meant to let that slot be changed at all. Swaps the
  // editable Datum/Vreme fields (Desktop) / steps 2-3 (Mobile) for a single read-only "Termin"
  // display, and saves straight through the normal (non-bulk, non-edit) handleSave path below.
  lockedSlot = false,
}) => {
  const { showAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  // This whole flow (Select Member → New Member, all 4 steps) is one mount-gated overlay —
  // the page behind it shouldn't scroll while it's open.
  useLockBodyScroll();

  // Edit mode: opened from Calendar → Zakazani članovi → Izmeni termin, with
  // the booking already in place. Reuses this exact same component/markup —
  // only the initial field values, title/button copy, and save behavior
  // (move existing booking vs. create a new one) differ.
  const isEditMode = !!editingAppointment;
  const originalHour = editingAppointment
    ? moment(editingAppointment.start).hour()
    : null;
  const originalDateStr = editingAppointment
    ? moment(editingAppointment.start).format("YYYY-MM-DD")
    : null;

  // Edit mode on Mobile: open straight on the Datum step (2), not the empty step-1 member
  // search — the member is already fixed (this is *their* booking, entered from Booked
  // Members), so there's nothing to pick there. Also skips the confirm/summary step 4 as a
  // starting point — the admin came here specifically to change something, so land them
  // straight on something editable instead of a screen whose only useful action is going back
  // anyway. The back arrow still reaches step 3 (Vreme) to change the time, but is disabled at
  // step 2 — see the header's back-button condition below — since going back further to step 1
  // would let the member be changed, which doesn't make sense for editing an existing booking.
  const [step, setStep] = useState(() => (editingAppointment ? 2 : 1));

  // Shared cache — reused instantly if the Members screen (or a previous
  // open of this same popup) already loaded it and it's still fresh; only
  // hits the network on a genuine cold start or once the cache goes stale.
  const { clients, packagesByClient, loading: loadingClients } = useMembers();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Same options/control as the Members page (MembersSortControl) — Desktop/Tablet's "Izaberi
  // člana" section shows the control next to its search field; Mobile shares this same state
  // and comparator but has no control of its own, so it just always uses the default (Ime,
  // A → Š), same as before this was made adjustable.
  const [sortField, setSortField] = useState("first_name");
  const [sortDir, setSortDir] = useState("asc");
  // Mobile only: whether the search field is focused (keyboard open). Driving the wizard's
  // shrunk-and-repositioned state off plain focus/blur instead of any viewport-resize API —
  // window.visualViewport's resize event does not reliably fire on keyboard open in Chrome for
  // iOS (a known WKWebView limitation), so neither that nor the interactive-widget viewport meta
  // flag were enough there. Focus/blur is a plain DOM event every browser fires reliably.
  const [searchFocused, setSearchFocused] = useState(false);
  // Blurring the search field to tap something else *in this same step* (a member row, "Dodaj
  // novog člana", or the footer "Sačuvaj"/"Dalje" button) must not un-shrink the modal
  // immediately — see handleSearchBlur below for why.
  const searchBlurTimeoutRef = useRef(null);
  const handleSearchFocus = () => {
    if (searchBlurTimeoutRef.current) {
      clearTimeout(searchBlurTimeoutRef.current);
      searchBlurTimeoutRef.current = null;
    }
    setSearchFocused(true);
  };
  // Delayed, not immediate like the focus side above: a tap that blurs this field to activate
  // another control on this same step needs that control's own tap to actually land first. An
  // immediate `setSearchFocused(false)` here snaps `.hf-naf-kb-open` (newAppointment.css) off
  // instantly, and that CSS swings the whole modal between two entirely different layouts (a
  // small one anchored to the top vs. a tall one anchored to the bottom) — happening mid-tap, it
  // moves whatever the finger was about to land on out from under it before the tap completes,
  // so the tap lands on nothing. Confirmed exactly this way: after searching for and tapping an
  // existing member (MemberResultRow's own onMouseDown already keeps the field focused through
  // that specific tap — see its comment — so this was never about the row itself), tapping the
  // footer "Sačuvaj" right after did blur the field and silently ate that tap. The same bug
  // already fixed once for the member-details popup's X button (memberPopup.css) and its gender
  // chips — this is the same family, just this popup's own search field. Cleared by
  // handleSearchFocus above if focus lands on the search field again within the delay (e.g.
  // typing resumes), so moving focus between fields still doesn't flicker the modal.
  const handleSearchBlur = () => {
    searchBlurTimeoutRef.current = setTimeout(() => {
      setSearchFocused(false);
      searchBlurTimeoutRef.current = null;
    }, 250);
  };
  useEffect(() => {
    return () => {
      if (searchBlurTimeoutRef.current) clearTimeout(searchBlurTimeoutRef.current);
    };
  }, []);
  const [selectedMember, setSelectedMember] = useState(
    () => editingClient || null
  );

  // Inline package status (see the "Član" section below) — opens the existing PackageAssignFlow
  // catalog popup (unchanged, same component "Izaberi paket" already used before this section
  // was redesigned around MemberPackageStatus/the inline used/total badge) when the selected
  // member has no active package. Assignment happens for real immediately on that popup's own
  // confirm step
  // (its already-existing behavior) — nothing staged/deferred here, so `selectedPkgStatus`
  // below (sourced from the same shared `packagesByClient` cache every other package UI in this
  // app reads from) is the one and only source of truth for what shows here and for whether
  // Sačuvaj is enabled.
  const [packageFlowOpen, setPackageFlowOpen] = useState(false);

  const [viewMonth, setViewMonth] = useState(
    moment(
      (editingAppointment ? editingAppointment.start : null) ||
        initialDate ||
        new Date()
    ).startOf("month")
  );
  // A tapped empty cell in the Mobile Week View (SchedulerTest.jsx) passes both initialDate and
  // initialHour together to preselect that exact slot — unlike the Desktop/Tablet-only
  // initialDate-seeds-selectedDate effect below, this applies on Mobile too, since it reflects
  // an explicit user pick (a specific cell tap), not just "the week currently being viewed".
  const [selectedDate, setSelectedDate] = useState(() => {
    if (editingAppointment) return moment(editingAppointment.start);
    if (initialHour != null && initialDate) return moment(initialDate);
    return null;
  });

  const [timeslotMap, setTimeslotMap] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedHour, setSelectedHour] = useState(() => {
    if (editingAppointment) return moment(editingAppointment.start).hour();
    return initialHour ?? null;
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Desktop/tablet-only: which field popover (if any) is open.
  const [openPopover, setOpenPopover] = useState(null); // "date" | "time" | null
  // Whether the open popover renders above or below its field — flips to
  // "up" when there isn't enough room below within the viewport (the field
  // sits near the bottom of the modal), so the calendar/hour grid never
  // gets clipped off-screen.
  const [popoverDirection, setPopoverDirection] = useState("down");
  const dateFieldRef = useRef(null);
  const timeFieldRef = useRef(null);
  const popoverRef = useRef(null);
  const modalRef = useRef(null);

  useLayoutEffect(() => {
    if (!openPopover) return;
    const fieldEl =
      (openPopover === "date" ? dateFieldRef : timeFieldRef).current;
    const popoverEl = popoverRef.current;
    if (!fieldEl || !popoverEl) return;
    const fieldRect = fieldEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - fieldRect.bottom;
    const spaceAbove = fieldRect.top;
    const popoverHeight = popoverEl.offsetHeight;
    setPopoverDirection(
      spaceBelow < popoverHeight + 16 && spaceAbove > spaceBelow
        ? "up"
        : "down"
    );
  }, [openPopover]);

  // Debounce the value that actually drives filtering — the input itself
  // stays bound to `search` so typing never feels delayed, only the
  // (potentially large) list re-filter + re-render is deferred.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Edit mode initially seeds `selectedMember` from the lightweight booking
  // row (client_id/first_name/last_name/gender only, no email/phone). Once
  // the full client list has loaded, upgrade it to the matching full record
  // so the selected-member card shows the same detail it would in Add mode.
  useEffect(() => {
    if (!isEditMode || loadingClients) return;
    const originalId = editingClient.client_id ?? editingClient.id;
    const full = clients.find((c) => c.id === originalId);
    if (full) setSelectedMember(full);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingClients]);

  // Dismiss a stale "slot is now full" error as soon as the user changes
  // any part of the selection.
  useEffect(() => {
    setSaveError(null);
  }, [selectedMember, selectedDate, selectedHour]);

  // Desktop/tablet only: default Datum to the calendar's currently-viewed
  // date, matching the reference (its date field is pre-filled, unlike
  // Vreme). Mobile's step wizard is untouched — it still requires an
  // explicit pick.
  useEffect(() => {
    if (isDesktopTablet && !selectedDate) {
      setSelectedDate(moment(initialDate || new Date()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktopTablet]);

  // Desktop/tablet only: Escape closes an open popover first, else the
  // whole modal.
  useEffect(() => {
    if (!isDesktopTablet) return;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (openPopover) {
        setOpenPopover(null);
      } else {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktopTablet, openPopover]);

  // Desktop/tablet only: clicking outside an open field popover closes it.
  useEffect(() => {
    if (!isDesktopTablet || !openPopover) return;
    const ref = openPopover === "date" ? dateFieldRef : timeFieldRef;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpenPopover(null);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isDesktopTablet, openPopover]);

  const filteredClients = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const pool = q
      ? clients.filter((c) => {
          const haystack = `${c.first_name || ""} ${c.last_name || ""} ${
            c.email || ""
          } ${c.phone || ""}`.toLowerCase();
          return haystack.includes(q);
        })
      : clients;
    return [...pool].sort(compareMembersBy(sortField, sortDir));
  }, [clients, debouncedSearch, sortField, sortDir]);

  // Same "which package is actually current, and its status" selection Members/index.js uses
  // (soonest-to-expire still-active package, falling back to the last one by end date) — not
  // just "most recently started", so this agrees with what the Members page itself would show
  // for the same client.
  const packageStatusFor = (clientId) =>
    selectCurrentPackage(packagesByClient[clientId] || []);

  // Recomputed live off `packagesByClient` (via useMembers(), already reactive) — so the moment
  // PackageAssignFlow assigns a package and updates the shared cache, this picks it up with no
  // extra plumbing, same as Members' own cards do.
  const selectedPkgStatus = useMemo(
    () =>
      selectedMember
        ? packageStatusFor(selectedMember.id ?? selectedMember.client_id)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedMember, packagesByClient]
  );

  // Bulk mode's own "Sačuvaj" (the member-selection step, before a slot is even picked) needs
  // the member to actually have something to book against — an already-active package with at
  // least one session left (`selectedPkgStatus.status` is only "active" once usedSessions <
  // totalSessions — see selectCurrentPackage). Otherwise the trainer could pick a slot for a
  // member with zero sessions available and hit the booking's own NO_AVAILABLE_PACKAGE error
  // only after already choosing a time. No "or a package was just staged" branch anymore —
  // assigning one via the section below is immediate (PackageAssignFlow's own existing
  // behavior), so by the time this is checked `selectedPkgStatus` already reflects it.
  const canSaveBulkMember =
    !!selectedMember && !saving && selectedPkgStatus?.status === "active";

  // "Termin: Čet 10 · 10:00" — lockedSlot's read-only replacement for the editable Datum/Vreme
  // fields (Desktop) / steps 2-3 (Mobile). Same weekday-abbreviation convention as the Booked
  // Members popup this flow is opened from (PopUpCard.jsx), via the same shared array, so the
  // label reads identically in both places.
  const lockedSlotLabel =
    lockedSlot && selectedDate && selectedHour != null
      ? `${WEEKDAY_SHORT[(selectedDate.day() + 6) % 7]} ${selectedDate.date()} · ${String(
          selectedHour
        ).padStart(2, "0")}:00`
      : "";

  // Sessions left in the active package (null when there's no active one) — drives both whether
  // the "running low" renew prompt shows and the label it carries.
  const activeSessionsLeft =
    selectedPkgStatus?.status === "active"
      ? selectedPkgStatus.totalSessions - selectedPkgStatus.usedSessions
      : null;
  const activeRunningLow =
    activeSessionsLeft != null && activeSessionsLeft < LOW_SESSIONS_THRESHOLD;

  // Shared between the Desktop "Član" section and the Mobile step-1 selected-member card —
  // replaces "Dodaj novog član" in both (bulk mode keeps that button instead, untouched).
  //
  // Shown when the member has no active package (the common case: they fall back to the
  // auto-created "First App Package" backfill placeholder) OR when their active package is
  // running low (`activeRunningLow` — fewer than LOW_SESSIONS_THRESHOLD sessions left), so the
  // trainer can assign a fresh one on the spot. Hidden entirely once there's a comfortably
  // stocked active package — the inline count in the member row is enough there.
  //
  // Reuses the exact same MemberPackageStatus "has a package" card shape Members' own card
  // shows (not the plain dashed pill) — same component, same props, just fed this member's
  // actual current package row with its name swapped for "Dodeli paket" via `nameOverride` (a
  // purely cosmetic, New-Appointment-only relabel; Members' own card never passes that prop).
  // The card's own count + "Važi do"/"Isteklo" line already tells the low-vs-none story, so the
  // label stays the same short "Dodeli paket" in both cases. Falls back to the component's own
  // empty-pill state on the rare member who has never had any package row at all. Opens the
  // same PackageAssignFlow catalog "Izaberi paket", with `quickAssign` — see the render below.
  const renderPackageSection = () => {
    if (selectedPkgStatus.status === "active" && !activeRunningLow) return null;
    return (
      <div className="hf-naf-pkg-section">
        <MemberPackageStatus
          currentPackage={selectedPkgStatus.currentPackage}
          status={selectedPkgStatus.status}
          validUntil={selectedPkgStatus.validUntil}
          usedSessions={selectedPkgStatus.usedSessions}
          totalSessions={selectedPkgStatus.totalSessions}
          nameOverride="Dodeli paket"
          onClick={() => setPackageFlowOpen(true)}
        />
      </div>
    );
  };

  // The selected member's package status folded straight into their row (name/contact | divider
  // | used/total + "N termina") instead of a separate card below it — driven by the same
  // `selectedPkgStatus` as everything else here, so it appears/disappears/updates exactly when
  // that does (member change, a package just assigned via the catalog, ...).
  const packageInlineBadge = selectedPkgStatus?.status === "active" && (
    <div className="hf-naf-pkg-inline">
      <span className="hf-naf-pkg-inline-count">
        {selectedPkgStatus.usedSessions}/{selectedPkgStatus.totalSessions}
      </span>
      <span className="hf-naf-pkg-inline-label">
        {selectedPkgStatus.totalSessions} termina
      </span>
    </div>
  );

  // Called only on an actual successful save (PopUpTest's `openPopup(client)` after onSave) —
  const monthDays = useMemo(() => buildMonthDays(viewMonth), [viewMonth]);

  const today = moment().startOf("day");

  const isSelectableDay = (day) => {
    if (!day) return false;
    if (day.isBefore(today, "day")) return false;
    if (day.day() === 0) return false;
    return true;
  };

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = selectedDate.format("YYYY-MM-DD");
    // Edit mode: landing back on the appointment's original day should keep
    // (or restore) its original hour selected instead of clearing it — the
    // member is already booked there.
    const isOriginalDay = isEditMode && dateStr === originalDateStr;
    // lockedSlot never changes `selectedDate` at all (its Datum/Vreme UI is a read-only "Termin"
    // display, not a picker — see renderDesktopModal/renderMobileWizard), but `selectedDate` is
    // still a real, populated moment (from `initialDate`) that still needs its slots fetched for
    // `handleSave`'s own `timeslotMap[selectedHour]` lookup — so this same mount-time run of this
    // effect still fires, and without this branch it would otherwise clear the `initialHour` it
    // was given right back to null the moment the fetch starts.
    const isLockedDay =
      lockedSlot &&
      initialHour != null &&
      initialDate &&
      dateStr === moment(initialDate).format("YYYY-MM-DD");
    const fetchSlots = async () => {
      setLoadingSlots(true);
      setSelectedHour(
        isOriginalDay ? originalHour : isLockedDay ? initialHour : null
      );
      try {
        const startOfWeek = selectedDate.clone().startOf("week");
        const response = await ApiMethods.get(
          `auth/bookings_per_client_timeslot?start=${startOfWeek.format(
            "YYYY-MM-DD"
          )}`
        );
        const map = {};
        (response || []).forEach((appointment) => {
          const start = moment.parseZone(appointment.start);
          if (start.format("YYYY-MM-DD") === dateStr) {
            map[start.hour()] = {
              id: appointment.id,
              start: start.format("YYYY-MM-DD HH:mm:ss"),
              max: appointment.max_bookings || 7,
              used: appointment.used_bookings || 0,
            };
          }
        });
        setTimeslotMap(map);
      } catch (err) {
        console.log(err);
        setTimeslotMap({});
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleSave = async () => {
    if (!selectedMember || !selectedDate || selectedHour == null) return;
    const slot = timeslotMap[selectedHour];
    if (!slot) return;

    const clientId =
      selectedMember.id != null ? selectedMember.id : selectedMember.client_id;

    if (isEditMode) {
      const originalClientId =
        editingClient.client_id != null
          ? editingClient.client_id
          : editingClient.id;
      const isSameSlot = slot.id === editingAppointment.id;
      const isSameClient = clientId === originalClientId;

      // Re-check capacity right before saving — the slot's used/max in
      // `timeslotMap` was fetched when the date was picked and may be stale
      // by the time Save is clicked. The member being edited is still
      // counted in their own original slot until this save lands, so don't
      // count them against themselves. Gated by the slot's absolute ceiling
      // (7 by default, narrowed when staff set a lower custom max_bookings
      // on this slot via "Promeni broj vežbača" — slot.max), not the plain
      // 6-on-a-morning-slot normal capacity — this is the Office App, which
      // is otherwise allowed to push a morning slot one member past normal.
      const effectiveUsed = isSameSlot ? Math.max(0, slot.used - 1) : slot.used;
      const { absoluteCapacity } = getSlotCapacityRules(moment(slot.start).hour(), slot.max);
      if (effectiveUsed >= absoluteCapacity) {
        setSaveError(
          "Ovaj termin je sada popunjen. Izaberite drugi termin."
        );
        return;
      }

      setSaving(true);
      try {
        const timeSlots = [];
        if (!isSameSlot || !isSameClient) {
          timeSlots.push({
            client_id: originalClientId,
            status: "canceled",
            start: editingAppointment.start,
            timeSlot_id: editingAppointment.id,
          });
          timeSlots.push({
            client_id: clientId,
            status: "book",
            start: slot.start,
            timeSlot_id: slot.id,
          });
        }
        if (timeSlots.length > 0) {
          await ApiMethods.post("auth/add_booking_cancelation", {
            timeSlots,
          });
        }
        showAlert("Termin je uspešno izmenjen.", "success");
        // Package usage is computed live from cal_bookings (no counter in the mutation's own
        // response to read off), so refresh just the affected client(s)' cached package rows
        // instead of the whole member list — the Members screen's used/total badge and the
        // package popup's "Predstojeći termini" list both read from this same cache.
        refreshClientPackages(clientId);
        if (!isSameClient) refreshClientPackages(originalClientId);
        // Tell the caller whether this save actually moved the member to a
        // different slot/timeslot — SchedulerTest.jsx uses this to decide
        // whether the Booked Members popup for the *old* slot should close
        // outright (moved) or just resync itself (edited in place).
        if (refresh) refresh({ slotChanged: !isSameSlot || !isSameClient });
        onClose();
      } catch (err) {
        console.log(err);
        if (err?.code === "NO_AVAILABLE_PACKAGE") {
          showAlert(err.message, "error");
        } else {
          showAlert("Greška prilikom izmene termina", "error");
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      const data = {
        timeSlots: [
          {
            client_id: clientId,
            status: "book",
            start: slot.start,
            timeSlot_id: slot.id,
          },
        ],
      };
      await ApiMethods.post("auth/add_booking_cancelation", data);
      showAlert("Termin je uspešno zakazan.", "success");
      // See the edit branch above — refresh just this client's cached package/usage rows so
      // Members and the package popup reflect the new booking without a full member reload.
      refreshClientPackages(clientId);
      if (refresh) refresh();
      onClose();
    } catch (err) {
      console.log(err);
      if (err?.code === "NO_AVAILABLE_PACKAGE") {
        showAlert(err.message, "error");
      } else {
        showAlert("Greška prilikom zakazivanja termina", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    !!selectedMember && !!selectedDate && selectedHour != null && !saving;

  const monthPicker = (
    <MonthCalendarGrid
      viewMonth={viewMonth}
      onPrevMonth={() => setViewMonth((m) => m.clone().subtract(1, "month"))}
      onNextMonth={() => setViewMonth((m) => m.clone().add(1, "month"))}
      monthDays={monthDays}
      today={today}
      selectedDate={selectedDate}
      isSelectableDay={isSelectableDay}
      onSelectDay={(day) => {
        setSelectedDate(day);
        setOpenPopover(null);
      }}
    />
  );

  const hourPicker = (
    <>
      {loadingSlots && <div className="hf-naf-empty">Učitavanje...</div>}
      {!loadingSlots && (
        <div className="hf-naf-hour-grid">
          {CANONICAL_HOURS.map((hour) => {
            const slot = timeslotMap[hour];
            const max = slot?.max || 7;
            const used = slot?.used || 0;
            // The member being edited is already counted in `used` for
            // their own current slot — don't hold that against them when
            // deciding whether it's still selectable.
            const isOriginalSlot =
              isEditMode &&
              selectedDate &&
              originalDateStr &&
              selectedDate.format("YYYY-MM-DD") === originalDateStr &&
              hour === originalHour;
            const effectiveUsed = isOriginalSlot
              ? Math.max(0, used - 1)
              : used;
            // Gated by the slot's absolute ceiling (7 by default, not the 6-on-a-morning-slot
            // normal capacity) — the Office App is allowed to push a morning slot one member
            // past its normal capacity, so a 6/6 morning chip must stay clickable up to 7, only
            // disabling once the (possibly narrowed, when staff set a lower custom `max` via
            // "Promeni broj vežbača") hard ceiling is actually reached.
            const isFull =
              !!slot && effectiveUsed >= getSlotCapacityRules(hour, max).absoluteCapacity;
            const isAvailable = !!slot && !isFull;
            const isSelected = selectedHour === hour;
            return (
              <button
                type="button"
                key={hour}
                disabled={!isAvailable}
                className={`hf-naf-hour-chip${isSelected ? " selected" : ""}${
                  isFull ? " full" : ""
                }${!slot ? " unavailable" : ""}${
                  isOriginalSlot ? " current" : ""
                }`}
                onClick={() => {
                  setSelectedHour(hour);
                  setOpenPopover(null);
                }}
              >
                <span className="hf-naf-hour-label">{hour}:00</span>
                <span className="hf-naf-hour-capacity">
                  {slot ? `${used}/${max}` : "—"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // Cold load only — a background refresh of already-cached data doesn't
  // block the dropdown with skeletons, it just keeps showing what's there.
  const showMemberSkeleton = loadingClients && clients.length === 0;

  const selectMember = (client) => {
    setSelectedMember(client);
    setSearch("");
  };

  const memberResults = (
    <>
      {showMemberSkeleton &&
        Array.from({ length: 4 }).map((_, i) => <MemberRowSkeleton key={i} />)}
      {!showMemberSkeleton && filteredClients.length === 0 && (
        <div className="hf-naf-empty">Nema rezultata</div>
      )}
      {!showMemberSkeleton &&
        filteredClients.map((client) => (
          <MemberResultRow
            key={client.id}
            client={client}
            pkg={packageStatusFor(client.id).currentPackage}
            isSelected={false}
            onSelect={selectMember}
          />
        ))}
    </>
  );

  // ================= Desktop/Tablet: single-page form =================
  const renderDesktopModal = () => (
    <div className="hf-naf-overlay">
      <div className="hf-naf-d-modal" ref={modalRef}>
        <div className="hf-naf-d-header">
          <h2>{isEditMode ? "Izmeni termin" : "Novi termin"}</h2>
          <button
            type="button"
            className="hf-naf-close"
            onClick={onClose}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="hf-naf-d-body">
          <div className="hf-naf-d-section">
            <label className="hf-naf-d-label">
              {bulkMode ? "Izaberi člana" : "Član"}
            </label>
            <div className="hf-naf-d-search-row">
              <div className="hf-naf-search">
                <SearchIcon sx={{ fontSize: 17 }} />
                <input
                  type="text"
                  placeholder="Ime, telefon ili email"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    if (selectedMember) setSelectedMember(null);
                  }}
                />
              </div>
              <MembersSortControl
                field={sortField}
                dir={sortDir}
                onSelect={(nextField, nextDir) => {
                  setSortField(nextField);
                  setSortDir(nextDir);
                }}
              />
            </div>

            {selectedMember && !search && (
              <div className="hf-naf-d-selected-member">
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "black",
                    backgroundColor:
                      selectedMember.gender === "Male" ? "#97dfff" : "#FF8E8B",
                  }}
                >
                  {selectedMember.first_name
                    ? selectedMember.first_name[0].toUpperCase()
                    : "?"}
                  {selectedMember.last_name
                    ? selectedMember.last_name[0].toUpperCase()
                    : "?"}
                </Avatar>
                <div className="hf-naf-member-info">
                  <span className="hf-naf-member-name">
                    {selectedMember.first_name} {selectedMember.last_name}
                  </span>
                  <span className="hf-naf-member-contact">
                    {selectedMember.email || selectedMember.phone || ""}
                  </span>
                </div>
                {packageInlineBadge}
                <button
                  type="button"
                  className="hf-naf-d-clear-member"
                  aria-label="Ukloni izabranog člana"
                  onClick={() => setSelectedMember(null)}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            )}

            {(!selectedMember || search) && (
              <div className="hf-naf-d-member-dropdown">{memberResults}</div>
            )}

            {selectedMember && !search && renderPackageSection()}
          </div>

          {lockedSlot && (
            <div className="hf-naf-d-row">
              <div className="hf-naf-d-field-wrap">
                <label className="hf-naf-d-label">Termin</label>
                <div className="hf-naf-d-field hf-naf-d-field-locked">
                  <span>{lockedSlotLabel}</span>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
                </div>
              </div>
            </div>
          )}

          {!bulkMode && !lockedSlot && (
          <div className="hf-naf-d-row">
            <div className="hf-naf-d-field-wrap" ref={dateFieldRef}>
              <label className="hf-naf-d-label">Datum</label>
              <button
                type="button"
                className="hf-naf-d-field"
                onClick={() =>
                  setOpenPopover((p) => (p === "date" ? null : "date"))
                }
              >
                <span>
                  {selectedDate
                    ? selectedDate.format("ddd, MMM D")
                    : "Izaberi datum"}
                </span>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
              </button>
              {openPopover === "date" && (
                <div
                  ref={popoverRef}
                  className={`hf-naf-d-popover hf-naf-date-picker${
                    popoverDirection === "up" ? " hf-naf-popover-up" : ""
                  }`}
                >
                  {monthPicker}
                </div>
              )}
            </div>

            <div className="hf-naf-d-field-wrap" ref={timeFieldRef}>
              <label className="hf-naf-d-label">Vreme</label>
              <button
                type="button"
                className="hf-naf-d-field"
                disabled={!selectedDate}
                onClick={() =>
                  setOpenPopover((p) => (p === "time" ? null : "time"))
                }
              >
                <span>
                  {selectedHour != null ? `${selectedHour}:00` : "Izaberi vreme"}
                </span>
                <AccessTimeOutlinedIcon sx={{ fontSize: 17 }} />
              </button>
              {openPopover === "time" && (
                <div
                  ref={popoverRef}
                  className={`hf-naf-d-popover hf-naf-time-picker${
                    popoverDirection === "up" ? " hf-naf-popover-up" : ""
                  }`}
                >
                  {hourPicker}
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {!bulkMode && saveError && (
          <div className="hf-naf-save-error">{saveError}</div>
        )}

        <div className="hf-naf-d-footer">
          <button type="button" className="hf-naf-d-cancel" onClick={onClose}>
            Otkaži
          </button>
          {bulkMode ? (
            <button
              type="button"
              className={`hf-naf-save-btn${saving ? " hf-loading" : ""}`}
              disabled={!canSaveBulkMember}
              onClick={() => onMemberChosen(selectedMember)}
            >
              {saving && <span className="hf-btn-spinner" />}
              Sačuvaj
            </button>
          ) : (
            <button
              type="button"
              className={`hf-naf-save-btn${saving ? " hf-loading" : ""}`}
              disabled={!canSave}
              onClick={handleSave}
            >
              {saving && <span className="hf-btn-spinner" />}
              {isEditMode ? "Sačuvaj izmene" : "Sačuvaj termin"}
            </button>
          )}
        </div>
      </div>

      {packageFlowOpen && (
        <PackageAssignFlow
          open
          member={selectedMember}
          quickAssign
          onClose={() => setPackageFlowOpen(false)}
          onAssigned={() => setPackageFlowOpen(false)}
        />
      )}
    </div>
  );

  // ================= Mobile: existing 4-step wizard (untouched) =================
  const renderMobileWizard = () => (
    <div className="hf-naf-overlay">
      <div
        className={`hf-naf-modal${
          step === 1 && searchFocused ? " hf-naf-kb-open" : ""
        }`}
      >
        <div className="hf-naf-header">
          {/* Edit mode starts on step 2 (Datum) with the member already fixed — no back button
              there, since step 1 (Izaberite člana) is exactly the thing editing shouldn't let
              you change. Steps 3/4 still go back one step as usual (to Datum, then Vreme). */}
          {step > 1 && !(isEditMode && step === 2) ? (
            <button
              type="button"
              className="hf-naf-back"
              onClick={() => setStep((s) => s - 1)}
              aria-label="Nazad"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
            </button>
          ) : (
            <span className="hf-naf-back-spacer" />
          )}
          <span className="hf-naf-title">{STEP_TITLES[step]}</span>
          <button
            type="button"
            className="hf-naf-close"
            onClick={onClose}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        {!bulkMode && !lockedSlot && (
        <div className="hf-naf-steps">
          {[1, 2, 3, 4].map((s) => (
            <span
              key={s}
              className={`hf-naf-dot${step === s ? " active" : ""}${
                step > s ? " done" : ""
              }`}
            />
          ))}
        </div>
        )}

        {/* lockedSlot never advances past step 1 (see the footer below) — this banner is the
            only place the trainer sees which slot they're adding into, since the Datum/Vreme
            steps are skipped entirely. */}
        {lockedSlot && (
          <div className="hf-naf-locked-slot-banner">
            <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
            <span>Termin: {lockedSlotLabel}</span>
          </div>
        )}

        {step === 1 && (
          <div className="hf-naf-search-bar">
            <div className="hf-naf-search">
              <SearchIcon sx={{ fontSize: 17 }} />
              <input
                type="text"
                placeholder="Ime, prezime, email ili telefon"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
              />
            </div>

          </div>
        )}

        <div className="hf-naf-body">
          {step === 1 && (
            <div className="hf-naf-member-list">
              {showMemberSkeleton &&
                Array.from({ length: 5 }).map((_, i) => (
                  <MemberRowSkeleton key={i} />
                ))}
              {!showMemberSkeleton && filteredClients.length === 0 && (
                <div className="hf-naf-empty">Nema rezultata</div>
              )}
              {!showMemberSkeleton &&
                filteredClients.map((client) => {
                  const isSel = selectedMember?.id === client.id;
                  // The package section is folded into the list right under the selected
                  // member's own row (not a separate box above the list) — it eases in with a
                  // height+fade+slide reveal and only renders when there's actually something
                  // to offer (no package, or an active one running low). The selected row
                  // itself swaps its plain package-name pill for the "X/Y · N termina" count.
                  const section = isSel ? renderPackageSection() : null;
                  return (
                    <React.Fragment key={client.id}>
                      <MemberResultRow
                        client={client}
                        pkg={packageStatusFor(client.id).currentPackage}
                        isSelected={isSel}
                        onSelect={setSelectedMember}
                        inlineBadge={isSel ? packageInlineBadge : null}
                      />
                      {section && (
                        <div className="hf-naf-m-pkg-reveal">{section}</div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          )}

          {step === 2 && <div className="hf-naf-date-picker">{monthPicker}</div>}

          {step === 3 && (
            <div className="hf-naf-time-picker">{hourPicker}</div>
          )}

          {step === 4 && (
            <div className="hf-naf-summary">
              <div className="hf-naf-summary-row">
                <span className="hf-naf-summary-label">Član</span>
                <span className="hf-naf-summary-value">
                  {selectedMember?.first_name} {selectedMember?.last_name}
                </span>
              </div>
              <div className="hf-naf-summary-row">
                <span className="hf-naf-summary-label">Datum</span>
                <span className="hf-naf-summary-value">
                  {selectedDate?.format("DD.MM.YYYY")}
                </span>
              </div>
              <div className="hf-naf-summary-row">
                <span className="hf-naf-summary-label">Vreme</span>
                <span className="hf-naf-summary-value">
                  {selectedHour}:00
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="hf-naf-footer">
          {bulkMode ? (
            <button
              type="button"
              className={`hf-naf-next-btn${saving ? " hf-loading" : ""}`}
              disabled={!canSaveBulkMember}
              onClick={() => onMemberChosen(selectedMember)}
            >
              {saving && <span className="hf-btn-spinner" />}
              Sačuvaj
            </button>
          ) : lockedSlot ? (
            // Nothing left to pick besides the member (Termin is fixed, shown in the banner
            // above) — Sačuvaj is available the moment one's selected, same step the trainer
            // never leaves.
            <>
              {saveError && (
                <div className="hf-naf-save-error">{saveError}</div>
              )}
              <button
                type="button"
                className={`hf-naf-save-btn${saving ? " hf-loading" : ""}`}
                disabled={!canSave}
                onClick={handleSave}
              >
                {saving && <span className="hf-btn-spinner" />}
                Sačuvaj termin
              </button>
            </>
          ) : (
            step < 4 && (
              <button
                type="button"
                className="hf-naf-next-btn"
                disabled={
                  (step === 1 && !selectedMember) ||
                  (step === 2 && !selectedDate) ||
                  (step === 3 && selectedHour == null)
                }
                onClick={() => setStep((s) => s + 1)}
              >
                Dalje
              </button>
            )
          )}
          {!lockedSlot && step === 4 && (
            <>
              {saveError && (
                <div className="hf-naf-save-error">{saveError}</div>
              )}
              <button
                type="button"
                className={`hf-naf-save-btn${saving ? " hf-loading" : ""}`}
                disabled={!canSave}
                onClick={handleSave}
              >
                {saving && <span className="hf-btn-spinner" />}
                {isEditMode ? "Sačuvaj izmene" : "Sačuvaj termin"}
              </button>
            </>
          )}
        </div>
      </div>

      {packageFlowOpen && (
        <PackageAssignFlow
          open
          member={selectedMember}
          quickAssign
          onClose={() => setPackageFlowOpen(false)}
          onAssigned={() => setPackageFlowOpen(false)}
        />
      )}
    </div>
  );

  return isDesktopTablet ? renderDesktopModal() : renderMobileWizard();
};

export default NewAppointmentFlow;
